// @flow
import React, { useContext } from 'react';
import { getIn, useFormikContext } from 'formik';
import { useParams } from 'react-router-dom';
import { Icon } from 'semantic-ui-react';
import { InfoMessageContainer } from '@presentational/messages/InfoMessageContainer';

import {
  CLUSTER_TABLEFLOW_FORM,
  CLUSTER_TABLEFLOW_STORAGE_TYPE_BACKEND_NAME,
  CLUSTER_TABLEFLOW_V2_MONTHLY_INPUTS_JSON_CONFIG,
  TABLEFLOW_STORAGE_TYPE_CONFLUENT,
  TABLEFLOW_STORAGE_TYPE_OWN,
  TABLEFLOW_VERSIONS,
} from '../../../constants';
import { ClusterInputTable } from '../cluster-input-table/ClusterInputTable';
import { PaddedAndRaisedSegment } from '../../presentational/PaddedAndRaisedSegment';
import { toastError } from '../../presentational/notifications/utils';
import { useUpdateClusterMutation } from '../../../service-definitions/estimates';
import { EstimateContext } from '../../../contexts/EstimateContext';
import { isAnythingEditedOnClusterPageBesidesTheGivenForm } from '../../views/utils';
import { SelectField } from '../../../formik-utils/FormFields';
import { Spacer } from '../../presentational/spacing/Spacer';
import { StyledSegmentWithBorderColor } from '../../../common-utils/styledComponents';

import { TABLEFLOW_ROWS_CONFIG_BY_VERSION } from './tableflow-config';

const SIZING_GUIDE_MESSAGE = (
  <>
    For assistance with Tableflow sizing, refer to the Configuration Guide in the Tableflow
    <sup>
      <Spacer x={0.25} />
      <Icon fitted={true} name="info circle" />
    </sup>
    <Spacer x={2} /> tooltip above.
  </>
);

// V2 only -- see TableflowUsageContainer for the legacy tab. Kept as its own component/Formik
// key (CLUSTER_TABLEFLOW_V2_MONTHLY_INPUTS_JSON_CONFIG) rather than a version branch, so changes
// to one version can't regress the other and neither needs the other's regression testing.
export const TableflowV2UsageContainer = () => {
  const rowsConfig = TABLEFLOW_ROWS_CONFIG_BY_VERSION[TABLEFLOW_VERSIONS.V2];

  const storageTypeOptions = [
    {
      name: TABLEFLOW_STORAGE_TYPE_CONFLUENT,
      display_name: 'Enable Confluent Storage',
    },
    {
      name: TABLEFLOW_STORAGE_TYPE_OWN,
      display_name: 'Use my own storage',
    },
  ];
  const { estimateId, clusterId } = useParams();
  const [updateCluster] = useUpdateClusterMutation();
  const estimate = useContext(EstimateContext);

  const { values, initialValues } = useFormikContext();
  const shouldFormBeDisabled = isAnythingEditedOnClusterPageBesidesTheGivenForm(
    values,
    initialValues,
    CLUSTER_TABLEFLOW_FORM
  );

  return (
    <PaddedAndRaisedSegment disabled={shouldFormBeDisabled}>
      <InfoMessageContainer message={SIZING_GUIDE_MESSAGE} />
      <ClusterInputTable
        AdditionalComponent={
          <>
            <Spacer y={40} />
            <StyledSegmentWithBorderColor compact={true}>
              <SelectField
                disabled={shouldFormBeDisabled}
                fieldDisplayName="Storage Type"
                fieldName={`${CLUSTER_TABLEFLOW_V2_MONTHLY_INPUTS_JSON_CONFIG}.${CLUSTER_TABLEFLOW_STORAGE_TYPE_BACKEND_NAME}`}
                fluid={false}
                options={storageTypeOptions}
                search={false}
              />
            </StyledSegmentWithBorderColor>
          </>
        }
        disabled={shouldFormBeDisabled}
        keyNameInValues={CLUSTER_TABLEFLOW_V2_MONTHLY_INPUTS_JSON_CONFIG}
        rowsConfig={rowsConfig}
        shouldDisableMonthWhenCorrespondingKafkaMonthIsDisabled={true}
        tableName="Tableflow Monthly Usage"
        updateFunc={async () => {
          const valuesToSendToBackend = getIn(
            values,
            CLUSTER_TABLEFLOW_V2_MONTHLY_INPUTS_JSON_CONFIG
          );
          const storageType = valuesToSendToBackend[CLUSTER_TABLEFLOW_STORAGE_TYPE_BACKEND_NAME];
          const tableflowInputs = Object.values(valuesToSendToBackend.months);
          const payload = {
            id: clusterId,
            estimate_id: estimateId,
            estimate_version: estimate?.inputs?.estimate_version,
            [CLUSTER_TABLEFLOW_V2_MONTHLY_INPUTS_JSON_CONFIG]: {
              [CLUSTER_TABLEFLOW_STORAGE_TYPE_BACKEND_NAME]: storageType,
              inputs: tableflowInputs,
            },
          };
          const { error } = await updateCluster({
            estimateId,
            clusterId,
            payload,
          });
          if (error) {
            toastError(error);
          }
        }}
      />
    </PaddedAndRaisedSegment>
  );
};
